// @flow
import * as React from 'react';

import Switcher from '../../basic-components/switcher';
import Status from '../stock-table/status';
import useAiModalClientChange from './use-ai-modal-client-change';
import Tooltip from '../stock-table/tooltip';
import ClockLoader from '../../basic-components/clock-loader';

import cs from '../stock-table/stock-row/publish-switcher/styles.pcss';

type SwitcherProps = {
  published: boolean,
  selected: boolean | 'unclear',
  hover: boolean,
  synchronizable: boolean,
  managed: boolean,
  domain?: string,
  notSynchronizableReasons?: string[],
  listingWithoutShowcase?: boolean,
  productId: string,
  showcaseId: string,
  checkedProducts?: string[],
  checkedVariations?: Set<string>,
};

const getSwitcherState = (selected) => {
  switch (selected) {
    case true:
      return 'on';
    case false:
      return 'off';
    case 'unclear':
      return 'unclear';
    default:
      return 'on';
  }
};

const renderUnpublishedStatus = () => (
  <Tooltip id="publishSwitcherMotorsUnpublished">
    <Status status="inactive" />
  </Tooltip>
);

const getSyncTipId = (synchronizable, notSynchronizableReasons) => {
  if (synchronizable) {
    return 'publishSwitcherNotManaged';
  }

  if (!notSynchronizableReasons || !notSynchronizableReasons.length) {
    return 'publishSwitcherNotManagedNotSync';
  }

  if (notSynchronizableReasons.find((r) => r === 'not_fixed_price')) {
    return 'publishSwitcherNotManagedAuction';
  }

  return 'publishSwitcherNotManagedNotSync';
};

/**
 * AI Products Modal 专用的 PublishSwitcher 组件
 *
 * 与 stock-table 的 PublishSwitcher 的区别：
 * 1. 使用 useAiModalClientChange 而不是 useClientChange
 * 2. 变更写入 aiModalChangesVar 而不是全局 changesVar
 * 3. 不影响 Stock 页面的状态
 */
export default function AiModalPublishSwitcher({
  published,
  selected,
  hover,
  synchronizable,
  managed,
  domain,
  notSynchronizableReasons,
  listingWithoutShowcase,
  productId,
  showcaseId,
  checkedProducts,
  checkedVariations,
}: SwitcherProps): React.Node {
  const isMassUpdate = productId === 'mass';

  const { onChange, value, changed } = useAiModalClientChange(
    'selected',
    selected,
    productId,
    undefined,
    showcaseId,
    checkedProducts,
    checkedVariations
  );

  const handleChange = (event) => {
    // For mass update, toggle between on/off (no unclear state after first click)
    if (isMassUpdate) {
      // Toggle between true and false
      const nextValue = changed ? !value : true;
      onChange(nextValue);
    } else {
      // Normal single product update
      onChange(event.target.checked);
    }
  };

  // For mass update, show switcher with unclear/on/off state
  if (isMassUpdate) {
    // If changed, show the new value; otherwise show 'unclear'
    const switcherState = changed ? getSwitcherState(value) : 'unclear';

    return (
      <Switcher
        state={switcherState}
        mode="success"
        className={cs.switcher}
        onChange={handleChange}
      />
    );
  }

  if (listingWithoutShowcase && !published) return renderUnpublishedStatus();

  if (published && !managed) {
    return (
      <Tooltip
        id={getSyncTipId(synchronizable, notSynchronizableReasons)}
        data={{ domain }}
      >
        <Status status={synchronizable ? 'ok' : 'error'} />
      </Tooltip>
    );
  }

  if (changed) {
    return (
      <Switcher
        state={getSwitcherState(value)}
        mode="success"
        className={cs.switcher}
        onChange={handleChange}
      />
    );
  }

  if (selected && !published) {
    return (
      <Tooltip id="publishSwitcherPublishing">
        <ClockLoader />
      </Tooltip>
    );
  }

  if (!selected && published) {
    return (
      <Tooltip id="publishSwitcherUnpublishing">
        <ClockLoader />
      </Tooltip>
    );
  }

  if ((hover && published) || !published) {
    return (
      <Tooltip
        id={`publishSwitcher${published ? 'On' : 'Off'}`}
        data={{ domain }}
      >
        <Switcher
          state={published ? 'on' : 'off'}
          mode="success"
          className={cs.switcher}
          onChange={handleChange}
        />
      </Tooltip>
    );
  }

  if (!hover && published) {
    return <Status status="ok" />;
  }

  return null;
}
